import { inject, Injectable } from '@angular/core';
import { SessionService } from '@monsido/core/session/session.service';
import { MonEventService } from '@monsido/services/mon-event/mon-event.service';
import { MeRepoService } from '@client/app/services/api/me-repo/me-repo.service';
import { ErrorHandler } from '@client/app/core/services/error-handler/error-handler';
import { OauthService } from '@monsido/oauth/oauth.service';
import { monsidoPath, oauthClientId, oauthClientSecret, oauthScopes, proxyApiPath } from '@monsido/core/constants/general.constant';
import { LocalStorageService } from '@monsido/angular-shared-components';
import { HttpLegacyService } from '@monsido/http/http-legacy-service/http-legacy.service';
import { User } from '@monsido/modules/models/api/user';
import { contextTokens } from '@monsido/ng2/services/request-auxiliary/request-auxiliary.service';
import { DateTimeService } from '@client/app/services/date-time/date-time.service';
import { TermsService } from '@client/app/modules/terms/terms.service';
import numeral from 'numeral';
import { UserLocaleService } from '@monsido/core/locale/services/locale.service';
import { Hotkey, HotkeysService } from 'angular2-hotkeys';
import { RollbarErrorHandlerService } from '@client/ng2/external/rollbar/rollbar-error-handler.service';
import { TranslateService } from '@client/app/services/translate/translate.service';
import { MeService } from '@client/app/services/entities/me/me.service';
import { MonLoadingService } from '@monsido/services/loading/services/loading.service';
import { safeClone } from '@client/app/shared/utils/object-utils';
import { MonLogService } from '@monsido/services/mon-log-service/mon-log.service';

@Injectable({
    providedIn: 'root',
})
export class LoginService {
    loadingUser: Promise<User | null> | null = null;
    savingAccessibilityMode = false;

    dateTimeService = inject(DateTimeService);
    termsService = inject(TermsService);
    userLocaleService = inject(UserLocaleService);
    hotkeysService = inject(HotkeysService);
    rollbarErrorHandlerService = inject(RollbarErrorHandlerService);
    translateService = inject(TranslateService);
    meService = inject(MeService);
    monLoading = inject(MonLoadingService);

    constructor (
        private sessionService: SessionService,
        private monEventsService: MonEventService,
        private meRepoService: MeRepoService,
        private errorHandler: ErrorHandler,
        private oauthService: OauthService,
        private localStorageService: LocalStorageService,
        private readonly monLogService: MonLogService,
        private httpLegacyService: HttpLegacyService,
    ) {
        this.monEventsService.addListener('force logout', () => {
            this.logout().catch();
        });
    }

    async getUser (): Promise<User | null> {
        if (this.sessionService.me) {
            return this.sessionService.me;
        }

        if (this.loadingUser) {
            return this.loadingUser;
        } else {
            this.loadingUser = this.loadUser();
            return this.loadingUser;
        }
    }

    async reloadUser () {
        return this.loadUser();
    }

    async loadUser (): Promise<User | null> {
        await this.monEventsService.run('beforeLoadUser');
        try {
            const data = await this.meRepoService.get(contextTokens.NO_GLOBAL);

            if (!data) {
                await this.monEventsService.run('newException', 'No user returned upon login', 'critical');
            }

            this.onUserLoaded(data, this.sessionService.isSudo);

            return data;
        } catch (response: unknown) {
            const r = response as { status?: number } & (User | null);
            if ((r.status ?? 0) !== 423) {
                if (!this.sessionService.me || !this.sessionService.customer) {
                    this.errorHandler.standardMsg('Missing user or customer', 'info');
                }
                await this.logout();
                return null;
            }
            return r as User | null;
        } finally {
            this.loadingUser = null;
        }
    }

    async logout (): Promise<void> {
        const token = this.getAuthToken();
        const bodyParams = {
            token: token,
            client_id: oauthClientId,
            client_secret: oauthClientSecret,
        };

        try {
            await this.httpLegacyService.post(`${monsidoPath}oauth/revoke.json`, bodyParams);
        } catch (e) {
            if (e instanceof Error) {
                this.monLogService.warn('Failed to revoke token on logout.');
            }
        }

        this.oauthService.clear();
        this.sessionService.clearSession();
        this.localStorageService.setItem('roles', [].toString());
        try {
            await this.monEventsService.run('logout');
        } catch (e) {
            if (e instanceof Error) {
                this.monLogService.warn('An error occurred during logout event handling.');
            }
        }
        window.onbeforeunload = null;
        const url = this.oauthService.getLogoutUrl();
        if (url) {
            this.oauthService.clearLogoutUrl();
            window.location.href = url;
        } else {
            window.location.href = `${monsidoPath}logout?redirect_uri=${this.getEncodedBaseUrl()}`;
        }
    }

    setup2FA (): void {
        window.location.href = this.oauthService.generateOTPUrl(
            monsidoPath,
            'setup',
            encodeURIComponent(window.location.pathname + window.location.search),
        );
    }

    getAuthorizeUrl (): string {
        return this.oauthService.getAuthUrlAndSetStateParam(monsidoPath, oauthClientId, oauthScopes);
    }

    getAuthToken (): string {
        return this.oauthService.getToken();
    }
    getRefreshToken (): string {
        return this.oauthService.getRefreshToken();
    }

    isLoggedIn (): boolean {
        return this.oauthService.isAuthenticated();
    }

    getApiPath (): string {
        return proxyApiPath;
    }

    setSudoSession () {
        this.oauthService.useMemoryStore();
        this.sessionService.isSudo = true;
    }

    private getEncodedBaseUrl (): string {
        const baseUrl = window.location.protocol + '//' + window.location.host;
        return encodeURIComponent(baseUrl);
    }

    private onUserLoaded (user: User, isSudo: boolean) {
        // TODO:NOW Implement the following lines
        // ng2SessionService.roles = $localStorage.roles || [];
        // amMoment.changeLocale(user.locale);

        if (user.locale) {
            this.dateTimeService.setLocale(user.locale);
            numeral.locale(this.userLocaleService.getNumeralValue(user.locale));
        }

        this.termsService.checkTermsAcceptance(user, isSudo);
        this.sessionService.setMe(user);
        this.rollbarErrorHandlerService.setConfigPayload({
            person: {
                id: user.id,
            },
        });
        // TODO:NOW Do we need to cleanup this hotkey?
        this.hotkeysService.add(
            new Hotkey(
                'shift+a',
                () => {
                    if (!this.savingAccessibilityMode) {
                        this.savingAccessibilityMode = true;
                        const me = safeClone(this.sessionService.me);
                        if (!me?.settings) {
                            return false;
                        }

                        this.monLoading.show();
                        me.settings.accessibility = !me.settings.accessibility;
                        this.meService
                            .update(me)
                            .then(() => {
                                this.monLoading.hide();
                            }, () => {})
                            .finally(() => {
                                this.savingAccessibilityMode = false;
                            });
                    }
                    return false;
                },
                [],
                this.translateService.getString('Enable / Disable Accessibility mode'),
            ),
        );
    }
}
